import styled, { css } from 'styled-components';
import AppearanceWrapper from '../_common/Controller/components/AppearanceWrapper/AppearanceWrapper';
import CoreRadio from '../_common/CoreRadio/CoreRadio';
import CoreCheckbox from '../_common/CoreCheckbox/CoreCheckbox';
import { selectableListTokens } from '../../tokens/components/SelectableList/tokens';

const spacing = {
  gutter: {
    padding: selectableListTokens.ContainerSpacingPaddingBaseDefault,
    gap: selectableListTokens.ContainerSpacingGapBaseDefault,
  },
  compact: {
    padding: selectableListTokens.ContainerSpacingPaddingCompactDefault,
    gap: selectableListTokens.ContainerSpacingGapCompactDefault,
  },
};

export const SelectableListWrapper = styled.div`
  ${({ theme }) =>
    theme.selectableListHorizontal &&
    css`
      display: flex;
    `}
`;

export const SelectableListRadio = styled(CoreRadio)`
  > ${AppearanceWrapper} {
    padding-top: 0;
    justify-content: center;
  }
`;

export const SelectableListCheckbox = styled(CoreCheckbox)`
  > ${AppearanceWrapper} {
    padding-top: 0;
    justify-content: center;
  }
`;

/* @internal */
export const StyledSelectableListOption = styled.label`
  background-color: ${selectableListTokens.ContainerBackgroundColorBaseDefault};
  border-radius: ${selectableListTokens.ContainerBorderRadiusBaseDefault};
  cursor: pointer;
  display: flex;
  outline: ${selectableListTokens.ContainerBorderWidthBaseDefault} solid
    ${selectableListTokens.ContainerBorderColorBaseDefault};
  // SelectableList uses outline instead of border to
  // prevent layout shifts when line is thicker in the selected state.
  // Because outline start after the border box, it overlaps the
  // box-shadow from the focus state.
  // The negative offset prevents the overlap, improving the focus state.
  outline-offset: -1px;
  transition: border 80ms ease-out, box-shadow 80ms ease-out;
  user-select: none;

  &:hover {
    outline: ${selectableListTokens.ContainerBorderWidthBaseHover} solid
      ${selectableListTokens.ContainerBorderColorBaseHover};
  }

  // When the internal input gets focus, the outer container gets focus
  &:has(:focus-visible) {
    box-shadow: 0 0 0 ${selectableListTokens.ContainerShadowWidthBaseKeyboardFocus}
      ${selectableListTokens.ContainerShadowColorBaseKeyboardFocus};
  }

  // Improves focus state for unchecked items
  ${({ theme }) =>
    !theme.selectableListOptionChecked &&
    !theme.selectableListOptionInvalid &&
    css`
    &:has(:focus-visible) {
      outline-color: ${selectableListTokens.ContainerBorderColorBaseKeyboardFocus};}
    }
  `}

  // Improved focus state for checked items
  ${({ theme }) =>
    theme.selectableListOptionChecked &&
    css`
      // The negative offset prevents thicker outline
      // from overlapping and therefore obscuring the focus shadow
      outline-offset: -2px;
    `}

  // Vertical
  ${({ theme }) =>
    !theme.selectableListHorizontal &&
    theme.selectableListSpacing &&
    css`
      margin-top: ${spacing[theme.selectableListSpacing].gap};

      &:first-of-type {
        margin-top: 0;
      }
    `}

  // Horizontal
  ${({ theme }) =>
    theme.selectableListHorizontal &&
    theme.selectableListSpacing &&
    css`
      flex: 1;
      flex-direction: column-reverse;
      margin-left: ${spacing[theme.selectableListSpacing].gap};

      &:first-of-type {
        margin-left: 0;
      }
    `}

  ${({ theme }) =>
    theme.selectableListOptionChecked &&
    !theme.selectableListOptionInvalid &&
    css`
      &,
      &:hover {
        outline: ${selectableListTokens.ContainerBorderWidthCheckedDefault} solid
          ${selectableListTokens.ContainerBorderColorCheckedDefault};
      }
    `}

  ${({ theme }) =>
    theme.selectableListOptionInvalid &&
    css`
      &,
      &:hover {
        outline-color: ${selectableListTokens.ContainerBorderColorInvalidDefault};
      }

      &:has(:focus-visible) {
        box-shadow: 0 0 0 ${selectableListTokens.ContainerShadowWidthInvalidKeyboardFocus}
          ${selectableListTokens.ContainerShadowColorInvalidKeyboardFocus};
      }
    `}

  ${({ theme }) =>
    theme.selectableListOptionChecked &&
    theme.selectableListOptionInvalid &&
    css`
      &,
      &:hover {
        outline: ${selectableListTokens.ContainerBorderWidthCheckedDefault} solid
          ${selectableListTokens.ContainerBorderColorInvalidDefault};
      }
    `}

  ${({ theme }) =>
    theme.selectableListOptionDisabled &&
    css`
      background-color: ${selectableListTokens.ContainerBackgroundColorBaseDisabled};
      color: ${selectableListTokens.ContainerFontColorDisabledDefault};
      cursor: not-allowed;

      &,
      &:hover {
        outline: ${selectableListTokens.ContainerBorderWidthDisabledDefault} solid
          ${selectableListTokens.ContainerBorderColorDisabledDefault};
      }
    `}

    ${({ theme }) =>
    theme.selectableListOptionDisabled &&
    theme.selectableListOptionChecked &&
    css`
      &,
      &:hover {
        outline: ${selectableListTokens.ContainerBorderWidthCheckedDefault} solid
          ${selectableListTokens.ContainerBorderColorCheckedDisabled};
      }
    `}
`;

export const SelectableListControl = styled.div`
  align-items: center;
  display: flex;

  ${({ theme }) =>
    theme.selectableListSpacing &&
    css`
      padding: ${spacing[theme.selectableListSpacing].padding};
    `}

  ${({ theme }) =>
    theme.selectableListHorizontal &&
    css`
      flex-direction: column;
    `}
`;

export const SelectableListBody = styled.div`
  ${({ theme }) =>
    theme.selectableListSpacing &&
    css`
      padding: ${spacing[theme.selectableListSpacing].padding};
    `}

  width: calc(100% - ${selectableListTokens.ControlSizeBaseDefault});

  ${({ theme }) =>
    theme.selectableListHorizontal &&
    css`
      height: 100%;
      width: auto;
    `}

  ${({ theme }) =>
    theme.selectableLstOptionDisabled &&
    css`
      pointer-events: not-allowed;
    `}
`;

// 66px comes from checkbox (20px) + padding (16px) + margin (16px)
export const SelectableListOptionContent = styled.div`
  margin-left: calc(66px);
  margin-top: 16px;
  margin-bottom: 16px;
`;
